import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface ScrollPosition {
  scrollTop: number;
  timestamp: number;
}

interface ScrollPositionContextType {
  saveScrollPosition: (key: string, scrollTop: number) => void;
  getScrollPosition: (key: string) => number | null;
  clearScrollPosition: (key: string) => void;
  clearAllScrollPositions: () => void;
}

const ScrollPositionContext = createContext<ScrollPositionContextType | undefined>(undefined);

const SCROLL_POSITION_EXPIRY = 30 * 60 * 1000; // 30 minutes (notes / multi-select can stay open a while)

export const ScrollPositionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scrollPositions, setScrollPositions] = useState<Map<string, ScrollPosition>>(new Map());
  const scrollPositionsRef = useRef<Map<string, ScrollPosition>>(new Map());

  const saveScrollPosition = useCallback((key: string, scrollTop: number) => {
    console.log(`📍 ScrollPositionContext: Saving scroll position for ${key}:`, scrollTop);
    const position: ScrollPosition = {
      scrollTop,
      timestamp: Date.now()
    };

    scrollPositionsRef.current.set(key, position);
    setScrollPositions(new Map(scrollPositionsRef.current));
  }, []);

  const getScrollPosition = useCallback((key: string): number | null => {
    const position = scrollPositionsRef.current.get(key);

    if (!position) {
      console.log(`📍 ScrollPositionContext: No scroll position found for ${key}`);
      return null;
    }

    // Check if position has expired
    const age = Date.now() - position.timestamp;
    if (age > SCROLL_POSITION_EXPIRY) {
      console.log(`📍 ScrollPositionContext: Scroll position for ${key} has expired (${age}ms old)`);
      scrollPositionsRef.current.delete(key);
      setScrollPositions(new Map(scrollPositionsRef.current));
      return null;
    }

    console.log(`📍 ScrollPositionContext: Retrieved scroll position for ${key}:`, position.scrollTop);
    return position.scrollTop;
  }, []);

  const clearScrollPosition = useCallback((key: string) => {
    console.log(`📍 ScrollPositionContext: Clearing scroll position for ${key}`);
    scrollPositionsRef.current.delete(key);
    setScrollPositions(new Map(scrollPositionsRef.current));
  }, []);

  const clearAllScrollPositions = useCallback(() => {
    console.log('📍 ScrollPositionContext: Clearing all scroll positions');
    scrollPositionsRef.current.clear();
    setScrollPositions(new Map());
  }, []);

  return (
    <ScrollPositionContext.Provider
      value={{
        saveScrollPosition,
        getScrollPosition,
        clearScrollPosition,
        clearAllScrollPositions
      }}
    >
      {children}
    </ScrollPositionContext.Provider>
  );
};

export const useScrollPosition = () => {
  const context = useContext(ScrollPositionContext);
  if (context === undefined) {
    throw new Error('useScrollPosition must be used within a ScrollPositionProvider');
  }
  return context;
};
